import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';

const CURRENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || '';
const CHECK_INTERVAL_MS = 60_000;
const RELOAD_GUARD_KEY = 'pmp-reloaded-for-build';

function versionUrl(): string {
  const base = process.env.PUBLIC_URL || '';
  return `${base}/version.json?t=${Date.now()}`;
}

async function readRemoteBuildId(): Promise<string | null> {
  const response = await fetch(versionUrl(), {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  return typeof data?.buildId === 'string' ? data.buildId : null;
}

async function bustHtmlCache(): Promise<void> {
  const base = process.env.PUBLIC_URL || '';
  const paths = Array.from(new Set([
    window.location.pathname || '/',
    `${base}/` || '/',
    `${base}/index.html`,
  ]));
  await Promise.all(
    paths.map((path) =>
      fetch(path, { cache: 'reload', headers: { 'Cache-Control': 'no-cache' } }).catch(() => undefined)
    )
  );
}

const AppVersionChecker: React.FC = () => {
  const [showManualRefresh, setShowManualRefresh] = useState(false);

  const checkForUpdate = useCallback(async () => {
    if (!CURRENT_BUILD_ID || process.env.NODE_ENV !== 'production') {
      return;
    }

    try {
      const remoteBuildId = await readRemoteBuildId();
      if (!remoteBuildId || remoteBuildId === CURRENT_BUILD_ID) {
        return;
      }

      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === remoteBuildId) {
        setShowManualRefresh(true);
        return;
      }

      sessionStorage.setItem(RELOAD_GUARD_KEY, remoteBuildId);
      await bustHtmlCache();
      window.location.reload();
    } catch {
      // Ignore network errors; the next interval will try again.
    }
  }, []);

  useEffect(() => {
    if (!CURRENT_BUILD_ID || process.env.NODE_ENV !== 'production') {
      return;
    }

    const interval = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', checkForUpdate);
    checkForUpdate();

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', checkForUpdate);
    };
  }, [checkForUpdate]);

  return (
    <Snackbar
      open={showManualRefresh}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        }
      >
        A new version is available. Please refresh to see the latest changes.
      </Alert>
    </Snackbar>
  );
};

export default AppVersionChecker;
